// ============================================================================
// MedCore Research — Editable content
// ----------------------------------------------------------------------------
// NOTE (Future CMS): Every export below maps to a Sanity document/array and is
// consumed by presentational components via props or direct import. When CMS
// is wired up, replace these constants with `await sanityClient.fetch(...)`
// queries that return the same shapes (see /src/types/content.ts).
// ============================================================================

import type {
  AudienceContent,
  CapabilitiesContent,
  ContactContent,
  HeroContent,
  IconCard,
  ModelContent,
  QualityContent,
  TextBlock,
} from "@/types/content";

// Professional stock imagery (replace with branded assets / CMS uploads later).
export const IMAGES = {
  hero:
    "https://images.unsplash.com/photo-1573497701175-00c200fd57f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwdGVhbSUyMG1lZXRpbmclMjBjbGluaWN8ZW58MHx8fGJsdWV8MTc4Mjc4MzM2OHww&ixlib=rb-4.1.0&q=85",
  about:
    "https://images.unsplash.com/photo-1536064479547-7ee40b74b807?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVhbSUyMG1lZXRpbmclMjBjbGluaWN8ZW58MHx8fGJsdWV8MTc4Mjc4MzM2OHww&ixlib=rb-4.1.0&q=85",
  toronto:
    "https://images.unsplash.com/photo-1653355612915-0478a3c4160b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHx0b3JvbnRvJTIwc2t5bGluZSUyMHdhdGVyZnJvbnQlMjBkYXlsaWdodHxlbnwwfHx8d2hpdGV8MTc4Mjc4MzM3NHww&ixlib=rb-4.1.0&q=85",
} as const;

// ------------------------------ Home: Hero ---------------------------------
export const HERO: HeroContent = {
  eyebrow: "MedCore Research",
  headline: "Global Clinical Research Site Management & Consulting",
  subheadline:
    "MedCore Research supports clinical sites, physicians, pharma and biotech companies, sponsors, CROs, and research partners by connecting suitable study opportunities with capable clinical sites and supporting quality-focused clinical trial operations.",
  primaryCta: { label: "Partner With MedCore", to: "/contact" },
  secondaryCta: { label: "Explore Our Services", to: "#who-we-serve" },
  backgroundImage: IMAGES.hero,
  highlights: [
    "Research readiness & study matching",
    "Feasibility, start-up & coordination",
    "Quality & audit readiness",
  ],
};

// ------------------------- Home: Brief Introduction ------------------------
export const HOME_INTRO: TextBlock = {
  title: "A practical partner for clinical research",
  paragraphs: [
    "MedCore Research is a Canadian-based clinical research site management and consulting organization focused on making clinical trial participation more practical, organized, and sustainable for healthcare providers.",
    "We support clinical sites with research readiness, study opportunity matching, feasibility, start-up, coordination, documentation, communication, and audit readiness. We also support pharma, biotech, sponsors, and CROs by helping identify suitable sites and maintain responsive, quality-focused site operations throughout the study lifecycle.",
  ],
};

// ----------------------------- Home: Who We Serve --------------------------
export const WHO_WE_SERVE: IconCard[] = [
  {
    icon: "stethoscope",
    title: "Clinical Sites & Physicians",
    description:
      "We help clinical sites participate in research while allowing physicians to remain focused on patient care.",
  },
  {
    icon: "flask",
    title: "Pharma, Biotech & CROs",
    description:
      "We support study teams by helping identify, prepare, and support suitable clinical sites.",
  },
  {
    icon: "handshake",
    title: "Research Partners",
    description:
      "We collaborate with healthcare providers and research stakeholders based on study needs, site capabilities, therapeutic focus, and partnership fit.",
  },
];

// --------------------------------- About -----------------------------------
export const ABOUT_PARAGRAPHS: string[] = [
  "MedCore Research is a Canadian-based clinical research site management and consulting organization supporting clinical trial operations, site partnerships, and study delivery.",
  "Our goal is to bridge the gap between patient care and clinical research by helping physicians and clinical sites participate in trials in a practical, organized, and compliant manner.",
  "We support clinical sites from research readiness and study matching to feasibility, study start-up, coordination, documentation, communication with sponsors/CROs, and audit readiness.",
  "For pharma, biotech companies, sponsors, and CROs, MedCore Research supports site identification, feasibility support, site preparation, start-up coordination, and ongoing site management support.",
  "Headquartered in Canada, MedCore Research works with clinical research partners based on study requirements, site capabilities, therapeutic focus, patient population, and collaboration fit.",
];

// -------------------------- For Sites & Physicians -------------------------
export const SITES_PHYSICIANS: AudienceContent = {
  heading:
    "Helping Physicians Focus on Patients While We Support Research Operations",
  intro: [
    "MedCore Research supports clinical sites so physicians can focus on patient care while we manage the operational, administrative, and coordination aspects of clinical research.",
    "We help sites assess their research readiness, identify suitable study opportunities, and match studies with site capabilities, patient population, therapeutic focus, and physician interests. From feasibility and study start-up to documentation support, patient visit coordination, sponsor/CRO communication, and audit readiness, our goal is to make clinical trial participation practical, organized, and sustainable for busy healthcare providers.",
  ],
  services: [
    {
      icon: "clipboard-check",
      title: "Research Readiness Assessment",
      description:
        "We help clinical sites evaluate whether they have the patient population, investigator interest, staff support, space, documentation processes, and operational capacity required to participate in clinical trials.",
    },
    {
      icon: "target",
      title: "Study Opportunity Matching",
      description:
        "We help identify studies that align with the site\u2019s capabilities, patient population, therapeutic focus, and physician interests.",
    },
    {
      icon: "file-search",
      title: "Feasibility Support",
      description:
        "We support feasibility questionnaires, site capability assessments, patient pool estimates, and communication with study teams.",
    },
    {
      icon: "rocket",
      title: "Study Start-Up Support",
      description:
        "We support essential document collection, regulatory and ethics coordination support, training records, delegation logs, CTA/budget communication, and activation tracking.",
    },
    {
      icon: "workflow",
      title: "Study Coordination Support",
      description:
        "We help with visit coordination, study documentation, query follow-up, vendor communication, sponsor/CRO communication, and issue escalation.",
    },
    {
      icon: "shield-check",
      title: "Quality & Audit Readiness",
      description:
        "We support GCP-aligned documentation, investigator site file readiness, training records, delegation logs, source documentation processes, and audit/inspection preparation.",
    },
  ],
};

// ------------------------ For Pharma, Biotech & CROs -----------------------
export const PHARMA_CROS: AudienceContent = {
  heading:
    "Supporting Site Identification, Feasibility, Start-Up, and Site Operations",
  intro: [
    "MedCore Research supports pharmaceutical and biotechnology companies, clinical trial sponsors, and CROs by identifying and preparing suitable clinical sites, supporting feasibility and start-up activities, and helping maintain organized, responsive, and quality-focused site operations throughout the study lifecycle.",
    "Our focus is to support practical site engagement, clear communication, timely follow-up, and operational readiness so that clinical trials can be delivered more efficiently at the site level.",
  ],
  services: [
    {
      icon: "building",
      title: "Site Identification & Feasibility Support",
      description:
        "We help identify suitable clinical sites based on patient population, therapeutic area, physician interest, site capabilities, and operational readiness.",
    },
    {
      icon: "rocket",
      title: "Site Start-Up Support",
      description:
        "We support document collection, site activation tracking, training record coordination, CTA/budget communication support, and communication between stakeholders.",
    },
    {
      icon: "gauge",
      title: "Site Management Support",
      description:
        "We help maintain site engagement, follow up on action items, support recruitment activities, coordinate operational needs, and assist with issue escalation.",
    },
    {
      icon: "user-plus",
      title: "Recruitment & Retention Support",
      description:
        "We help sites develop practical, ethical, and protocol-aligned recruitment and retention strategies based on their patient population and clinic workflows.",
    },
    {
      icon: "scroll-text",
      title: "Quality Oversight Support",
      description:
        "We support documentation quality, issue tracking, escalation, investigator site file readiness, and audit preparation.",
    },
  ],
};

// -------------------------------- Our Model --------------------------------
export const OUR_MODEL: ModelContent = {
  heading: "A Practical Partnership Model for Clinical Research Sites",
  intro: [
    "MedCore Research works as an operational partner between clinical sites and study stakeholders. Our role is to help sites become research-ready, identify suitable study opportunities, coordinate study start-up, support study conduct, and maintain quality-focused documentation and communication.",
  ],
  columns: [
    {
      icon: "stethoscope",
      title: "For Physicians",
      description:
        "Physicians focus on patient care, medical oversight, patient safety, and investigator responsibilities.",
    },
    {
      icon: "building",
      title: "For Clinical Sites",
      description:
        "Sites receive support with research readiness, study matching, feasibility, start-up, coordination, documentation, communication, and audit readiness.",
    },
    {
      icon: "handshake",
      title: "For Pharma, Biotech & CROs",
      description:
        "Study teams gain access to motivated, prepared, and supported clinical sites with practical operational support.",
    },
  ],
};

// ---------------- Therapeutic Areas & Site Capabilities --------------------
export const CAPABILITIES: CapabilitiesContent = {
  heading: "Connecting the Right Studies With the Right Clinical Sites",
  intro: [
    "At MedCore Research, we connect the right studies with the right clinical sites. We assess each site's patient demographics, physician expertise, therapeutic interests, and operational readiness to identify research opportunities that are both feasible and impactful.",
    "By matching protocols to sites where they are most relevant, we help sponsors accelerate recruitment, improve study quality, and ensure successful trial delivery.",
  ],
  areas: [
    "Diabetes",
    "Obesity",
    "Cardiovascular diseases",
    "Vaccines",
    "Respiratory diseases",
    "Dermatology",
    "Pediatric",
    "Women's health",
    "Mental health",
  ],
  note: "Our therapeutic focus continues to evolve based on study opportunities, site capabilities, physician expertise, and patient needs.",
};

// ---------------------------- Quality & Compliance -------------------------
export const QUALITY: QualityContent = {
  heading: "Quality-Focused Clinical Trial Support",
  intro: [
    "MedCore Research supports clinical trial activities with a focus on Good Clinical Practice principles, patient safety, protocol compliance, documentation quality, timely communication, and audit readiness.",
    "We help sites establish practical research workflows that support compliant, organized, and quality-focused study conduct.",
  ],
  pillars: [
    {
      icon: "heart-pulse",
      title: "Patient Safety and Rights",
      description:
        "Supporting trial conduct with patient safety, informed participation, and ethical research principles at the center.",
    },
    {
      icon: "clipboard-check",
      title: "Protocol Compliance",
      description:
        "Helping sites understand and follow protocol requirements, study procedures, and visit expectations.",
    },
    {
      icon: "folder-archive",
      title: "Documentation Quality",
      description:
        "Supporting accurate, complete, and timely documentation, including source documents, training records, delegation logs, and investigator site file materials.",
    },
    {
      icon: "users",
      title: "Clear Roles and Responsibilities",
      description:
        "Helping sites maintain clear documentation of investigator oversight, delegated tasks, and operational responsibilities.",
    },
    {
      icon: "message",
      title: "Communication and Escalation",
      description:
        "Supporting timely communication between sites, sponsors, CROs, vendors, and other research stakeholders.",
    },
    {
      icon: "badge-check",
      title: "Audit and Inspection Readiness",
      description:
        "Helping sites prepare for monitoring visits, audits, sponsor reviews, and inspections.",
    },
  ],
};

// --------------------------------- Contact ---------------------------------
export const CONTACT: ContactContent = {
  heading: "Partner With MedCore Research",
  intro:
    "Interested in bringing clinical research to your practice or exploring site partnership opportunities? MedCore Research welcomes collaboration with clinical sites, physicians, pharma and biotech companies, sponsors, CROs, and research partners.",
  options: [
    {
      icon: "stethoscope",
      title: "For Clinical Sites & Physicians",
      description:
        "Interested in participating in clinical research? Contact MedCore Research to discuss research readiness, study opportunity matching, and site support services.",
    },
    {
      icon: "flask",
      title: "For Pharma, Biotech & CROs",
      description:
        "Looking for suitable clinical sites or site support services? Contact MedCore Research to discuss feasibility, site identification, start-up support, and trial operations support.",
    },
    {
      icon: "handshake",
      title: "For Research Partners",
      description:
        "We welcome collaboration opportunities with healthcare providers, research organizations, and clinical trial stakeholders based on study needs, site capabilities, and partnership fit.",
    },
  ],
};

import type { BlogPost } from "@/types/content";

// ============================================================================
// BLOGS (structured, MD Trials-style) \u2014 placeholder posts
// ============================================================================
export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "why-gcp-certification-is-essential",
    title: "Why Good Clinical Practice (GCP) Certification Is Essential in Clinical Research",
    excerpt:
      "Clinical research is built on trust. Learn how GCP certification protects participants, supports reliable data, and strengthens clinical trial quality.",
    image: IMAGES.hero,
    category: "Good Clinical Practice",
  },
];
